import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Link as LinkIcon, Upload, Search, ExternalLink, Download } from "lucide-react";

/**
 * Admin panel — iter 96d.
 * Maps FDP product codes (e.g. FP324) to their real per-product page URL on
 * fabricadeprofile.ro. Client feedback 27.07.2026: the "Comandă" deeplink was
 * landing on the category page (with a non-existent `#cp-{code}` anchor)
 * instead of the actual product page. Once an admin fills in the URLs here,
 * `_build_aggregated_shop_links` uses them directly.
 *
 * Two ways to bulk-import:
 *  1. Paste a CSV block: `code,url` per line (comma OR tab separator)
 *  2. Upload a .csv file with the same shape
 *
 * Individual rows can also be edited inline in the table.
 */
export default function ProductUrlsManager() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_codes: 0, total_with_url: 0, total_missing_url: 0 });
  const [filter, setFilter] = useState("");
  const [showOnlyMissing, setShowOnlyMissing] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [editing, setEditing] = useState({}); // { CODE: url }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/catalog/product-urls");
      setRows(r.data?.codes || []);
      setSummary({
        total_codes: r.data?.total_codes || 0,
        total_with_url: r.data?.total_with_url || 0,
        total_missing_url: r.data?.total_missing_url || 0,
      });
    } catch (e) {
      toast.error("Nu am putut încărca lista codurilor produs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const parseCsv = (text) => {
    const lines = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    const out = [];
    for (const line of lines) {
      // Skip header row if the first field literally reads "code"
      if (out.length === 0 && /^code[,\t]/i.test(line)) continue;
      const parts = line.split(/[,\t]/).map((p) => p.trim());
      if (parts.length < 2) continue;
      const [code, ...rest] = parts;
      const url = rest.join(",").trim(); // rejoin in case URL contains commas
      if (code) out.push({ code: code.toUpperCase(), url });
    }
    return out;
  };

  const submitMapping = async (mapping) => {
    if (!mapping.length) {
      toast.error("Nicio pereche cod-URL de importat");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/catalog/product-urls", { mapping });
      const { updated_codes = 0, skipped_missing = [], total_photos_touched = 0 } = r.data || {};
      const skipCount = skipped_missing.length;
      toast.success(
        `${updated_codes} coduri actualizate (${total_photos_touched} fotografii)` +
        (skipCount ? ` — ${skipCount} sărite (cod inexistent sau URL invalid)` : "")
      );
      setBulkText("");
      setEditing({});
      await load();
    } catch (e) {
      toast.error("Import eșuat: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImport = async () => {
    const mapping = parseCsv(bulkText);
    await submitMapping(mapping);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const mapping = parseCsv(text);
    await submitMapping(mapping);
    e.target.value = ""; // reset
  };

  const handleInlineSave = async (code) => {
    const url = (editing[code] ?? "").trim();
    await submitMapping([{ code, url }]);
  };

  const filteredRows = rows.filter((r) => {
    if (showOnlyMissing && r.product_url) return false;
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (r.code || "").toLowerCase().includes(f) ||
           (r.category_slug || "").toLowerCase().includes(f);
  });

  return (
    <div className="space-y-6" data-testid="product-urls-manager">
      {/* Header + summary */}
      <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <LinkIcon size={16} className="text-orange-500" />
          <h3 className="font-outfit font-bold text-white">URL-uri per produs (deeplink Comandă)</h3>
        </div>
        <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
          Fiecare cod de produs FDP (ex: FP324) trebuie asociat cu URL-ul paginii de produs
          de pe fabricadeprofile.ro. Fără această mapare, butonul &laquo;Comandă&raquo; din randări
          duce la pagina de categorie, nu la pagina specifică a produsului.
        </p>
        <div className="flex flex-wrap gap-4 text-xs">
          <div><span className="text-zinc-500">Total coduri:</span> <span className="text-white font-mono">{summary.total_codes}</span></div>
          <div><span className="text-zinc-500">Cu URL:</span> <span className="text-emerald-400 font-mono">{summary.total_with_url}</span></div>
          <div><span className="text-zinc-500">Lipsă URL:</span> <span className="text-rose-400 font-mono">{summary.total_missing_url}</span></div>
        </div>
      </div>

      {/* Bulk import */}
      <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-4">
        <div className="flex items-center gap-2 mb-2">
          <Upload size={16} className="text-orange-500" />
          <h3 className="font-outfit font-bold text-white text-sm">Import bulk (CSV)</h3>
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Format: <code className="text-zinc-300">cod,url</code> per rând (poate începe cu header &laquo;code,url&raquo;).
          Suportă separator virgulă sau tab.
        </p>
        <textarea
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={"FP324,https://www.fabricadeprofile.ro/produse-exterior/brau-decorativ-exterior-din-polistiren/brau-decorativ-fatada-fp324-dimensiune-250x70mm\nFP401,https://www.fabricadeprofile.ro/..."}
          rows={5}
          className="w-full font-mono text-xs bg-zinc-950 border border-white/10 rounded-md p-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/50"
          data-testid="product-urls-bulk-textarea"
        />
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <Button
            onClick={handleBulkImport}
            disabled={saving || !bulkText.trim()}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="product-urls-bulk-submit"
          >
            {saving ? <><Loader2 size={14} className="animate-spin" /> Importă</> : <>Importă text</>}
          </Button>
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={handleFileUpload}
              className="hidden"
              data-testid="product-urls-file-input"
            />
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 hover:border-orange-500/50 text-xs text-zinc-300 bg-zinc-950">
              <Upload size={14} /> sau încarcă fișier .csv
            </span>
          </label>
          <a
            href="/downloads/fdp_product_urls_template.csv"
            download
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 hover:border-emerald-500/50 text-xs text-emerald-300 bg-zinc-950 ml-auto"
            data-testid="product-urls-download-template"
          >
            <Download size={14} /> Descarcă template CSV (311 coduri)
          </a>
        </div>
      </div>

      {/* Filter + list */}
      <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-4">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Caută după cod sau categorie..."
              className="pl-9 bg-zinc-950 border-white/10 text-white h-9"
              data-testid="product-urls-filter"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyMissing}
              onChange={(e) => setShowOnlyMissing(e.target.checked)}
              className="accent-orange-500"
              data-testid="product-urls-filter-missing"
            />
            Doar codurile fără URL
          </label>
          <span className="text-xs text-zinc-500 ml-auto">
            Afișate: {filteredRows.length} / {rows.length}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-zinc-400">
            <Loader2 size={18} className="animate-spin mr-2" /> Încărcare...
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-950 border-b border-white/10">
                <tr className="text-left text-zinc-500 uppercase tracking-wider">
                  <th className="p-2 font-medium">Cod</th>
                  <th className="p-2 font-medium">Categorie</th>
                  <th className="p-2 font-medium">Tip</th>
                  <th className="p-2 font-medium">URL produs</th>
                  <th className="p-2 font-medium w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const editVal = editing[r.code] ?? (r.product_url ?? "");
                  const dirty = editVal !== (r.product_url ?? "");
                  return (
                    <tr key={r.code} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-2 font-mono text-orange-400">{r.code}</td>
                      <td className="p-2 text-zinc-400">{r.category_slug || "—"}</td>
                      <td className="p-2 text-zinc-500">{r.sim_type}</td>
                      <td className="p-2">
                        <Input
                          value={editVal}
                          onChange={(e) => setEditing({ ...editing, [r.code]: e.target.value })}
                          placeholder="https://www.fabricadeprofile.ro/..."
                          className="h-7 text-xs bg-zinc-950 border-white/10 text-white font-mono"
                          data-testid={`product-urls-input-${r.code}`}
                        />
                      </td>
                      <td className="p-2">
                        {r.product_url && !dirty ? (
                          <a
                            href={r.product_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-xs"
                            data-testid={`product-urls-open-${r.code}`}
                          >
                            <ExternalLink size={12} /> Vezi
                          </a>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => handleInlineSave(r.code)}
                            disabled={saving || !dirty}
                            className="h-7 text-[10px] bg-orange-500 hover:bg-orange-600 text-white px-2"
                            data-testid={`product-urls-save-${r.code}`}
                          >
                            Salvează
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredRows.length === 0 && (
              <div className="text-center py-8 text-zinc-500 text-xs">Niciun cod găsit</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
